/**
 * This file will contain all typing sins we do.
 */

import { ChangeEvent, SyntheticEvent, useCallback, useMemo } from "react";

import { castArray, omit } from "lodash";
import {
  Control,
  ControllerFieldState,
  ControllerRenderProps,
  FieldArrayPath,
  FieldError,
  FieldPath,
  FieldValues,
  useFieldArray,
  UseFieldArrayProps,
  UseFieldArrayRemove,
  UseFieldArrayReturn,
  UseFormRegisterReturn,
} from "react-hook-form";

function _valueAsNumber(value: any): number | string {
  const number = parseFloat(value);
  return isNaN(number) ? "" : number;
}

export interface HandleChangeOptions {
  valueAsNumber?: boolean;
  maxDecimalPlace?: number;
}

export function handleChange(
  handler: (value: any) => void,
  opts?: HandleChangeOptions
) {
  return (event: ChangeEvent<HTMLInputElement> | any) => {
    let value: any = "target" in event ? event.target.value : event;

    if (opts?.valueAsNumber === true) {
      value = _valueAsNumber(value);
    }

    if (opts?.maxDecimalPlace != null) {
      const decimalPlaceRegex = new RegExp(
        `^-?\\d+(?:\\.\\d{0,${opts?.maxDecimalPlace}})?$`
      );
      if (value !== "" && !decimalPlaceRegex.test(value)) return;
    }

    handler(value);
  };
}

export function muiField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  field: ControllerRenderProps<TFieldValues, TName>,
  opts?: {
    valueAsNumber?: boolean;
    maxDecimalPlace?: number;
  }
) {
  const props = { ...field };

  if (opts?.valueAsNumber === true) {
    props.value = _valueAsNumber(field.value) as any;
    props.onChange = (e) => {
      const val =
        typeof e === "string"
          ? e
          : (e as ChangeEvent<HTMLInputElement>).target.value;
      return field.onChange(_valueAsNumber(val) as any);
    };
  }
  if (opts?.maxDecimalPlace != null) {
    const decimalPlaceRegex = new RegExp(
      `^-?\\d+(?:\\.\\d{0,${opts?.maxDecimalPlace}})?$`
    );
    const onChange = props.onChange;
    props.onChange = (e) => {
      const val =
        typeof e === "string"
          ? e
          : (e as ChangeEvent<HTMLInputElement>).target.value;
      if (val.trim() === "" || decimalPlaceRegex.test(val))
        onChange(val as any);
    };
  }

  return props;
}

export function muiDate<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(field: ControllerRenderProps<TFieldValues, TName>) {
  return { ...field, onChange: field.onChange as (value: Date | null) => void };
}

export function muiCheckbox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(field: ControllerRenderProps<TFieldValues, TName>) {
  const { value, onChange, ...props } = field;

  const onChangeHandler = (
    event: SyntheticEvent<Element, Event>,
    checked: boolean
  ) => {
    onChange(checked as any);
  };
  return { ...props, checked: value, onChange: onChangeHandler };
}

export function muiError(fieldState: ControllerFieldState): {
  error: boolean;
  helperText: string;
} {
  return muiFieldError(fieldState.error);
}

export function muiFieldError(error?: FieldError): {
  error: boolean;
  helperText: string;
} {
  return {
    error: !!error,
    helperText: error?.message ?? " ",
  };
}

/**
 * Passes `ref` to `inputRef` instead.
 */
export function muiRegister(
  register: UseFormRegisterReturn
): UseFormRegisterReturn {
  return register;
}

/**
 * `useFieldArray` configured for using with `id` and `_destroy`.
 * Changes from normal `useFieldArray`:
 * * `remove` function now updates `_destroy` and doesn't remove the item from the array if item
 *   has `id`. Otherwise if no `id` meaning a new record, it still removes it from the array.
 * * Defaults to `key` for `keyName` to not clash with `id`.
 */
export function useUpdateableFieldArray<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = "key"
>(
  props: UseFieldArrayProps<TFieldValues, TFieldArrayName, TKeyName>
): UseFieldArrayReturn<TFieldValues, TFieldArrayName, TKeyName> {
  const methods = useFieldArray<TFieldValues, TFieldArrayName, TKeyName>({
    keyName: "key" as TKeyName,
    ...props,
  });

  const fields = useMemo(
    () => methods.fields.filter((f) => (f as any)._destroy !== true),
    [methods.fields]
  );
  const remove: UseFieldArrayRemove = useCallback(
    (index?: number | number[]) => {
      if (index == null) methods.remove();

      const indexes = castArray(index);
      indexes.forEach((index) => {
        const value: any = methods.fields[index];
        const isNew = value.id == null;
        if (isNew) {
          methods.remove(index);
        } else {
          const previousValue = omit(value, props.keyName ?? "key"); // remove keyName from the properties
          methods.update(index, { ...previousValue, _destroy: true } as any);
        }
      });
    },
    [methods.fields, methods.update, methods.remove]
  );

  const returned = useMemo(
    () => ({ ...methods, fields, remove }),
    [methods, fields, remove]
  );
  return returned;
}

/**
 * Allows values with string type (those that comes directly from inputs) to be
 * passed to fields expecting number type. Doesn't parse as number. Unsafe af.
 *
 * TO BE USED ONLY IN REACT-HOOK-FORM AND MUST BE CAST WITH VALIDATION SCHEMA.
 */
export function valueAsNumber(value: string): number {
  return value as unknown as number;
}

export type InferValues<TControl extends Control<any, any>> =
  TControl extends Control<infer U, any> ? U : never;
